<template>
	<div
		ref="athleteTagsCt"
		class="table list--container is-dark"
		:class="{'is-mobile': isMobileWebApp}"
	>
		<div class="athlete-tags list-body tbody">
			<TagRow
				:entry="columnLabels"
				:columns="columns"
				:can-select="canSelect"
				:checked="selectAll"
				:checkbox-always-visible="true"
				class="thead"
				@selection-changed="handleSelectAllToggle"
			/>
			<AthleteTagGrid
				v-if="athlete"
				:items="groupedAthleteTags[athlete.id]"
				:selection-map="selectionMap"
				:columns="columns"
				:checkbox-always-visible="checkboxAlwaysVisible"
				:can-select="canSelect"
				@selection-changed="handleSelectionChanged"
				@row-click="handleRowClick"
			/>
			<div v-else>
				<AthleteTagGrid
					v-for="athlete in athletesWithTags"
					:key="`athlete-tags-${athlete.id}`"
					:items="groupedAthleteTags[athlete.id]"
					:selection-map="selectionMap"
					:columns="columns"
					:checkbox-always-visible="checkboxAlwaysVisible"
					:can-select="canSelect"
					@selection-changed="handleSelectionChanged"
					@row-click="handleRowClick"
				>
					<template #header>
						<div class="tr athlete-header-row">
							<div class="spacer" />
							<div class="td first">
								<Checkbox
									:id="`athlete-selector-${athlete.id}`"
									:value="isSelectedAthlete(athlete)"
									:theme="`dark`"
									:size="`small`"
									@change="handleAthleteSelectionChanged({selected: $event, athlete})"
								/>
								<div class="athlete-icon">
									<img
										v-if="athlete.imageUrl"
										:src="athlete.imageUrl"
										:alt="athlete.name"
									>
									<div
										v-else
										class="icon-wrapper"
									>
										<Icon
											icon-name="users-wm"
											size="md"
										/>
									</div>
								</div>
							</div>
							<div class="td">
								{{ athlete.name }}
							</div>
						</div>
					</template>
				</AthleteTagGrid>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import TagRow from '@videotools/js/one/components/TagList/TagRow';
import { TAG_NAME_RUN } from '@videotools/js/api/entities/TagButton';
import { mapGetters } from 'vuex';
import AthleteTagGrid from '@videotools/js/one/components/TagList/AthleteTagGrid';
import Checkbox from '@components/checkbox/Checkbox';
import { ATHLETE_UNKNOWN_ENTITY, ATHLETE_UNKNOWN_KEY } from '@videotools/js/constants/athletes';

export default {
	name:       'AthletesTagList',
	components: { Checkbox, AthleteTagGrid, TagRow },
	props:      {
		athlete: {
			type: Object,
		},
		allAthleteTags: {
			type:    Array,
			default: () => [],
		},
		canSelect: {
			type:    Boolean,
			default: true,
		},
		checkboxAlwaysVisible: {
			type:    Boolean,
			default: false,
		},
		rowContextMenuItems: {
			type:    Array,
			default: null,
		},
		sortDir: {
			type:    String,
			default: 'desc',
		},
	},
	data() {
		return {
			selectionMap:     {},
			selectedAthletes: [],
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:   'app/isMobileWebApp',
			event:            'event/event',
			isVod:            'event/isVod',
			sportName:        'event/sportName',
			participants:     'event/participants',
			isForParticipant: 'tags/isForParticipant',
			isForEvent:       'tags/isForEvent',
			activeFilters:    'tagFilters/activeFilters',
			canEditEventTag:  'permissions/canEditEventTag',
		}),
		columnLabels() {
			return {
				id:       this.headerRowId,
				action:   this.actionLabel,
				time:     this.timeLabel,
				duration: this.durationLabel,
			};
		},
		headerRowId() {
			return `head-${Math.round(Math.random() * 1000)}`;
		},
		columns() {
			return [
				{
					key:   'action',
					label: this.actionLabel,
					size:  this.getColumnSize('action'),
				},
				{
					key:   'time',
					label: this.timeLabel,
					size:  this.getColumnSize('time'),
				},
				{
					key:     'duration',
					label:   this.durationLabel,
					size:    this.getColumnSize('duration'),
					visible: !this.isMobileWebApp,
				},
			];
		},
		columnsAthlete() {
			return [{
				key: 'name',
			}];
		},
		items() {
			return this.groupedAthleteTags;
		},
		eventParticipants() {
			return [
				...this.participants,
				ATHLETE_UNKNOWN_ENTITY,
			];
		},
		groupedAthleteTags() {
			const ret = {};
			for (const athlete of this.eventParticipants) {
				ret[athlete.id] = this.getGroupedAthleteTags(athlete.id === ATHLETE_UNKNOWN_KEY ? null : athlete.id, this.sortDir);
			}
			return ret;
		},
		selectAll: {
			get() {
				const values = Object.values(this.selectionMap);
				if (values.length < this.visibleTags.length) {
					return false;
				}
				return values.length > 0 && values.every(Boolean);
			},
			set(value) {
				if (value) {
					this.initSelectionMap(true);
				} else {
					this.initSelectionMap(false);
				}
				this.emitChange();
			},
		},
		selectionSize() {
			return Object.values(this.selectionMap).filter(selected => selected).length;
		},
		hasSomeSelected() {
			return this.selectionSize > 0 && this.selectionSize < this.allAthleteTags.length;
		},
		selected() {
			const items = [];

			for (const [itemId, selected] of Object.entries(this.selectionMap)) {
				if (selected) {
					const item = this.allAthleteTags.find(it => it.id == itemId);
					if (item) {
						items.push(item);
					}
				}
			}

			return items;
		},
		hasSelection() {
			return this.selected.length > 0;
		},
		athletesWithTags() {
			return this.eventParticipants.filter(athlete => {
				return this.groupedAthleteTags[athlete.id]?.length > 0;
			});
		},
		visibleTags() {
			return this.athlete ? this.getAthleteTags(this.athlete.id) : this.allAthleteTags;
		},
	},
	methods: {
		initSelectionMap(selected = false) {
			this.selectionMap = Object.fromEntries(
				this.visibleTags.map((item) => [item.id, selected])
			);
			this.selectedAthletes = selected ? [...this.eventParticipants] : [];
		},
		syncSelectionMap() {
			this.initSelectionMap();
			for (const item of this.selectedItems) {
				if (!this.selectionMap[item.id]) {
					Vue.set(this.selectionMap, item.id, true);
				}
			}
		},
		handleRowClick(row) {
			this.$emit('row-click', row);
		},
		handleSelectionChanged(option) {
			Vue.set(this.selectionMap, option.entry.id, option.selected);
			this.emitChange();
		},
		emitChange() {
			this.$emit('selection-changed', this.selected);
		},
		handleSelectAllToggle(ev) {
			this.selectAll = ev.selected;
		},
		handleAthleteSelectionChanged({ selected, athlete }) {
			const toAdd = selected ? [athlete] : [];
			this.selectedAthletes = [...this.selectedAthletes.filter(a => a.id !== athlete.id), ...toAdd];
			const filteredTags = this.visibleTags.filter(evtag => {
				if (athlete.id === ATHLETE_UNKNOWN_KEY) {
					return !evtag.player?.id;
				}
				return evtag.player?.id === athlete.id;
			});

			for (const evTag of filteredTags) {
				this.handleSelectionChanged({ entry: evTag, selected: selected });
			}
		},
		isSelected(item) {
			return !!this.selectionMap[item.id];
		},
		getColumnSize(columnKey) {
			switch (columnKey) {
			case 'action':
				return !this.isMobileWebApp ? 55 : 75;

			case 'time':
				return !this.isMobileWebApp ? 20 : 25;

			case 'duration':
				return 25;
			}
		},
		getGroupedAthleteTags(athleteId, sortDir) {
			const ret = [];
			const athleteTags = this.getAthleteTags(athleteId);
			const runTags = athleteTags
				.filter(tagWrapper => {
					return tagWrapper.tag.name === TAG_NAME_RUN && (tagWrapper.player?.id || null) === athleteId;
				});

			const names = this.generateRunTagNames(runTags);
			const subtagsAdded = [];

			for (const runTag of runTags) {
				const endTimestamp = runTag.tag.endTimestamp;
				const startTimestamp = runTag.tag.startTimestamp;

				// get the sub tags that fit inside the interval
				const subtags = athleteTags.filter(evtag => {
					return evtag.tag.name !== TAG_NAME_RUN &&
						evtag.timestamp >= startTimestamp &&
						evtag.timestamp < endTimestamp &&
						evtag.player?.id === athleteId &&
						!subtagsAdded.includes(evtag.id);
				});
				// always sort subtags by timestamp ASC
				subtags.sort((a, b) => a.timestamp <= b.timestamp ? -1 : 1);

				ret.push({
					mainTag: { ...runTag, action: names[runTag.id] }, // set custom tag name to run tags
					subtags,
				});
				subtagsAdded.push(...subtags.map(evtag => evtag.id));
			}

			// add any remaining athlete tags that couldn't fit inside any run interval
			const tagsLeft = athleteTags.filter(t => t.tag.name !== TAG_NAME_RUN && !subtagsAdded.includes(t.id));
			ret.push(...tagsLeft.map(t => ({
				mainTag: t,
				subtags: [],
			})));

			// sort the main tags again
			const cmp = sortDir === 'asc' ? -1 : 1;
			ret.sort((a, b) => a.mainTag.timestamp < b.mainTag.timestamp ? cmp : -cmp);

			return ret;
		},
		generateRunTagNames(runTags) {
			const sortedTags = [...runTags].sort((a, b) => a.tag.startTimestamp < b.tag.startTimestamp ? -1 : 1);
			return sortedTags.reduce((acc, t, idx) => {
				return { ...acc, [t.id]: `Run ${idx + 1}` };
			}, {});
		},
		getAthleteTags(athleteId) {
			return this.allAthleteTags.filter(evTag => {
				return (evTag.player?.id || null) === athleteId;
			});
		},
		isSelectedAthlete(athlete) {
			return this.selectedAthletes.indexOf(athlete) >= 0;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.athlete-tags {
	.thead {
		border-bottom: 1px solid var(--color-grey-850);
		border-top: 1px solid var(--color-grey-850);
	}

	.athlete-header-row {
		.spacer {
			padding-left: 21px;
			.is-mobile & {
				padding-left: 7px;
			}
		}

		.td {
			display: flex;
			align-items: center;
			justify-content: right;
			max-height: 48px;
			padding-left: 11px;

			&.first {
				padding: 0;
				width: 24px;
				display: flex;
				justify-content: center;
				align-items: center;

				.list--container.is-mobile & {
					width: 35px;
				}

				.checkbox {
					display: none;
				}

				.athlete-icon {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 24px;
					height: 24px;

					img {
						width: 100%;
						height: 100%;
						border-radius: 50%;
						object-fit: cover;
					}
				}
			}
		}

		@media(hover: hover) {
			&:hover {
				.td.first {
					.athlete-icon {
						display: none;
					}

					.checkbox {
						display: initial;
					}
				}
			}
		}
	}
}

</style>
